import React, { Component } from "react"
import PropTypes from "prop-types"
import { drag, select, event, scaleLinear } from "d3"

import { Box } from "../../ui/Box"
import { Button } from "../../ui/Button"

import {
  requestInterval,
  getValue,
  getPublicValue,
  getTooltipValue,
  getDomain,
  getHandlePosition,
  getSnapPoints,
} from "./helpers"

class Slider extends Component {
  static propTypes = {
    // minimum value for the slider
    min: PropTypes.number,
    // maximum value for the slider
    max: PropTypes.number,
    // allow slider to pick only from the provided array of values
    customDomain: PropTypes.arrayOf(PropTypes.number),
    // provide selected value, defaults to the middle of the range
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.number),
    ]),
    // fires whenever value changes, value is the argument
    onChange: PropTypes.func,
    // fires when drag starts, value is the argument
    onDragStart: PropTypes.func,
    // fires when drag ends, value is the argument
    onDragEnd: PropTypes.func,

    // interpolate by step on playing/dragging or jump by snapStep/customDomain
    interpolate: PropTypes.bool,
    // increment value by the step when playing/dragging if interpolate=true
    step: PropTypes.number,
    // increment value by the snapStep when playing/dragging if interpolate=false
    // also snap to it after realising drag
    snapStep: PropTypes.number,
    // show little dots on snapSteps
    showSnapHints: PropTypes.bool,

    // decide if slider is playable
    playMode: PropTypes.bool,
    // duration interval for each value increment, in milliseconds
    duration: PropTypes.number,
    // start playing automatically on first render
    autoPlay: PropTypes.bool,
    // start from the beginning when the end is reached
    loop: PropTypes.bool,
    // fires when playing starts, value is the argument
    onPlayEnd: PropTypes.func,
    // fires when playing ends, value is the argument
    onPlayStart: PropTypes.func,

    // default "auto" shows tooltip when interacting with slider
    tooltip: PropTypes.oneOf(["auto", "on", "off"]),
    // show tooltip above or below the handle
    tooltipPosition: PropTypes.oneOf(["top", "bottom"]),
    // allows to format value displayed in the tooltip, value is the argument
    tooltipFormat: PropTypes.func,

    // default "auto" shows first and last ticks
    tickValues: PropTypes.oneOf(["auto", "on", "off"]),
    // allows to format tick values, value is the argument
    tickFormat: PropTypes.func,

    /**
     * Adjusts Slider's theme applying
     * border colors to play button, handle[s], tooltip and snap hints
     * and background color to progress in range mode
     */
    color: PropTypes.string,

    /**
     * Specify vertical margins
     */
    my: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Specify top margin
     */
    mt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Specify bottom margin
     */
    mb: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Specify padding
     */
    p: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Specify horizontal padding
     */
    px: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Specify vertical padding
     */
    py: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Specify top padding
     */
    pt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Specify bottom padding
     */
    pb: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Specify right padding
     */
    pr: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Specify left padding
     */
    pl: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array,
    ]),
    /**
     * Slider variant color
     */
    colorScheme: PropTypes.string,

    trackBg: PropTypes.string,
    trackHeight: PropTypes.number,
    rangeBg: PropTypes.string,
    handleBorder: PropTypes.string,
    handleBg: PropTypes.string,
  }

  static defaultProps = {
    min: 0,
    max: 100,

    interpolate: true,
    step: 0.05,
    snapStep: 1,
    showSnapHints: false,

    playMode: false,
    duration: 500,
    autoPlay: false,
    loop: false,

    tooltip: "auto",
    tooltipPosition: "top",

    tickValues: "auto",

    colorScheme: "primary",

    trackBg: `gray.100`,
  }

  constructor(props) {
    super()

    this.state = {
      value: getValue(props.value, props),
      isActive: false,
      playInterval: null,
      handleSize: 0,
    }

    this.scale = scaleLinear().domain(getDomain(props))
  }

  componentDidMount() {
    const isRS = this.isRangeSlider()
    select(this.track).call(
      drag().on("start", this.onDragStart()).on("end", this.onDragEnd)
    )
    select(this.handle).call(
      drag()
        .on("start", this.onDragStart(0))
        .on("drag", isRS ? this.onDrag(0) : this.onChange)
        .on("end", this.onDragEnd)
    )
    if (isRS) {
      select(this.handle1).call(
        drag()
          .on("start", this.onDragStart(1))
          .on("drag", this.onDrag(1))
          .on("end", this.onDragEnd)
      )
      select(this.rangeTrack).call(
        drag()
          .on("start", this.onRangeDragStart)
          .on("drag", this.onRangeDrag)
          .on("end", this.onRangeDragEnd)
      )
    }
    this.updateDimensions()
    if (this.props.autoPlay) this.handlePlayToggle()
    window.addEventListener("resize", this.updateDimensions)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { value } = nextProps
    this.scale.domain(getDomain(nextProps))
    this.setState(s => ({ value: getValue(value, nextProps, s) }))
  }

  componentDidUpdate() {
    this.scale.domain(getDomain(this.props))
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions)
  }

  updateDimensions = () => {
    const width = this.sliderHolder.offsetWidth
    const handleSize = this.handle.offsetHeight
    this.scale.range([0, width])
    this.setState(() => ({ handleSize }))
  }

  onChange = (value = this.scale.invert(event.x)) => {
    value = getValue(value, this.props, this.state)

    if (value[0] === this.state.value[0] && value[1] === this.state.value[1])
      return
    if (this.props.onChange) this.props.onChange(getPublicValue(value))
    this.setState(() => ({ value }))
  }

  onDragStart = id => () => {
    this.setState(() => ({ isActive: true }))

    let value = this.state.value
    const isRS = this.isRangeSlider()
    const ev = this.scale.invert(event.x)
    const handleIndex =
      isRS && typeof id === "undefined"
        ? Math.abs(value[0] - ev) <= Math.abs(value[1] - ev)
          ? 0
          : 1
        : id

    value = isRS ? this.getRangeValue(handleIndex) : [ev]

    if (this.props.onDragStart)
      this.props.onDragStart(getPublicValue(value, this.props, this.state))

    this.onChange(value)
  }

  onDrag = id => () => {
    this.onChange(this.getRangeValue(id))
  }

  onDragEnd = () => {
    this.setState(() => ({ isActive: false }))
    const value = getValue(this.state.value, this.props, this.state)
    this.onChange(value)
    if (this.props.onDragEnd) this.props.onDragEnd(getPublicValue(value))
  }

  onRangeDragStart = () => {
    this.setState(() => ({ isActive: true }))
    // get the distance between:
    // - left handle and clicked point
    // - clicked point and right handle
    this.rangeDragOffset = [
      event.x - this.scale(this.state.value[0]),
      this.scale(this.state.value[1]) - event.x,
    ]
    // used to determine if drag occured
    this.rangeDragStartX = event.x
  }

  onRangeDrag = () => {
    if (!this.rangeDragOffset) return

    const domain = getDomain(this.props)
    const [left, right] = this.rangeDragOffset
    const value = [
      this.getRangeValue(0, this.scale.invert(event.x - left))[0],
      this.getRangeValue(1, this.scale.invert(event.x + right))[1],
    ]
    if (value[0] <= domain[0] || value[1] >= domain[1]) return
    this.onChange(value)
  }

  onRangeDragEnd = () => {
    // allow to click on the progress track to set value
    if (Math.abs(event.x - this.rangeDragStartX) <= 10) this.onDragStart()()
    this.rangeDragOffset = null
    this.onDragEnd()
  }

  getRef = name => {
    return ref => {
      if (ref) this[name] = ref
    }
  }

  getTicks = props => {
    const { tickValues, customDomain, showSnapHints } = props

    if (tickValues === "on")
      return showSnapHints
        ? getSnapPoints(props)
        : customDomain || this.scale.ticks()
    else if (tickValues === "auto") return getDomain(props)
    else return null
  }

  handlePlayToggle = () => {
    const { playInterval, value } = this.state

    if (!playInterval) this.play(value)
    else this.stopPlay(value)
  }

  stopPlay = value => {
    const { playInterval } = this.state
    if (playInterval) playInterval.clear()

    this.setState(
      () => ({ playInterval: null, isActive: false }),
      () => {
        value = getValue(value, this.props, this.state)
        this.onChange(value)
        if (this.props.onPlayEnd) this.props.onPlayEnd(getPublicValue(value))
      }
    )
  }

  play = value => {
    const { duration, onPlayStart } = this.props
    const playInterval = requestInterval(this.incrementValue, duration)
    const max = getDomain(this.props)[1]
    const isRS = this.isRangeSlider()
    const isMax = value[isRS ? 1 : 0] === max

    if (isMax) value = this.getRestartedValue(value)

    if (onPlayStart) onPlayStart(getPublicValue(value, this.props, this.state))

    this.setState(
      () => ({
        playInterval,
        isActive: true,
      }),
      () => {
        if (isMax) this.onChange(value)
        else this.incrementValue(value)
      }
    )
  }

  incrementValue = (value = this.state.value) => {
    const { loop, step, snapStep, customDomain, interpolate } = this.props
    const max = getDomain(this.props)[1]
    const isRS = this.isRangeSlider()

    if (value[isRS ? 1 : 0] < max) {
      value = value.map((v, i) =>
        interpolate
          ? isRS
            ? this.getRangeValue(i, v + step)[i]
            : v + step
          : customDomain
          ? customDomain.find(c => c > v)
          : v + snapStep
      )
      this.onChange(value)
    } else if (loop) this.onChange(this.getRestartedValue(value))
    else this.stopPlay(value)
  }

  getRestartedValue = value => {
    const { customDomain, snapStep } = this.props
    const left = getDomain(this.props)[0]
    const right =
      this.isRangeSlider() && customDomain
        ? customDomain[
            customDomain.indexOf(value[1]) - customDomain.indexOf(value[0])
          ]
        : left + (value[1] - value[0]) / snapStep

    return right ? [left, right] : [left]
  }

  // i is the index in the value array 0 || 1, we can treat it as truthy/falsy
  // if dValue is missing it will be calculated from the scale and event.x
  getRangeValue = (i, nVal = this.scale.invert(event.x)) => {
    const { value: val } = this.state
    const { snapStep, customDomain } = this.props
    let ci
    let cds

    nVal = [i ? val[0] : nVal, i ? nVal : val[1]]

    // make sure we have a range between the values, they can't be equal
    // it's either a snapStep difference or two values from customDomain
    if (customDomain) {
      cds = customDomain.filter(c => (i ? c <= val[0] : c >= val[1]))
      ci = customDomain.indexOf(i ? cds[cds.length - 1] : cds[0]) + (i ? 1 : -1)
      ci = customDomain[ci]
      if ((!i && nVal[0] >= ci) || (i && nVal[1] <= ci)) nVal[i] = ci
    } else {
      if (!i && nVal[i] + snapStep >= val[1]) nVal[i] = val[1] - snapStep
      if (i && nVal[i] - snapStep <= val[0]) nVal[i] = val[0] + snapStep
    }

    return nVal
  }

  isRangeSlider = () => this.state.value.length > 1

  render() {
    const {
      min,
      max,
      tooltip,
      tooltipPosition,
      playMode,
      showSnapHints,
      tickFormat,
      mt,
      mb,
      my,
      p,
      pt,
      pb,
      pr,
      pl,
      px,
      py,
      colorScheme,
      trackBg,
      trackHeight = 4,
      rangeBg,
      handleBorder,
      handleBg,
    } = this.props
    const { value, isActive, playInterval, handleSize } = this.state
    const ticks = this.getTicks(this.props)
    const snapPoints = showSnapHints && getSnapPoints(this.props)
    const handlePosition = getHandlePosition(value, this.props)
    const tooltipValue = getTooltipValue(value, this.props)
    const isRS = this.isRangeSlider()

    return (
      <Box
        position="relative"
        display="flex"
        direction="row"
        overflow="visible"
        mt={mt}
        mb={mb}
        my={my}
        p={p}
        pt={pt}
        pb={pb}
        pr={pr}
        pl={pl}
        px={px}
        py={py}
      >
        {playMode && (
          <Button
            onClick={this.handlePlayToggle}
            type="button"
            tabIndex="0"
            position="relative"
            top="-22px"
            size="md"
            borderRadius="full"
            colorScheme={colorScheme}
          >
            <svg
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%,-50%)",
              }}
              width="12"
              height="12"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="0"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 12 12"
            >
              {playInterval ? (
                <rect x="1" y="1" width="10" height="10" />
              ) : (
                <path d="M 12,6 2,12 2,0 z" />
              )}
            </svg>
          </Button>
        )}

        <Box
          ref={this.getRef("sliderHolder")}
          position="relative"
          flex="auto"
          ml={playMode ? "1rem" : 0}
          mr={"12px"}
        >
          <Box
            className="sldr-background"
            bg={trackBg}
            position="relative"
            borderRadius={`${trackHeight}px`}
            height={`${trackHeight}px`}
            top={`-${(trackHeight - 4) / 2}px`}
            width="100%"
          />
          {isRS && (
            <Box
              position="absolute"
              bg={`${rangeBg || colorScheme}.500`}
              borderRadius={`${trackHeight}px`}
              height={`${trackHeight}px`}
              top="0px"
              left={`${handlePosition[0]}%`}
              width={`${handlePosition[1] - handlePosition[0]}%`}
            />
          )}

          <Button
            ref={this.getRef("handle")}
            className="sldr-handle"
            aria-valuenow={value[0]}
            aria-valuemin={min}
            aria-valuemax={isRS ? value[1] : max}
            aria-disabled="false"
            role="slider"
            tabIndex="0"
            size="xs"
            position="absolute"
            left={`${handlePosition[0]}%`}
            zIndex="2"
            borderRadius="full"
            marginLeft="-12px"
            top="-10px"
            colorScheme={colorScheme}
            bg={handleBg}
            transition="none"
            border={handleBorder ? handleBorder : ""}
          />
          {isRS && (
            <Button
              ref={this.getRef("handle1")}
              size="xs"
              className="sldr-handle"
              aria-valuenow={value[1]}
              aria-valuemin={value[0]}
              aria-valuemax={max}
              aria-disabled="false"
              role="slider"
              tabIndex="0"
              position="absolute"
              left={`${handlePosition[1]}%`}
              zIndex="2"
              borderRadius="full"
              marginLeft="-10px"
              top="-10px"
              colorScheme={colorScheme}
              bg={handleBg}
              transition="none"
              border={handleBorder ? handleBorder : ""}
            />
          )}

          {tooltip !== "off" && (
            <Box
              p="0.1875rem 0.5rem"
              mt="-1rem"
              fontSize="0.875rem"
              bg={`${colorScheme}.500`}
              color={`${colorScheme}.50`}
              borderRadius="sm"
              transition="opacity 0.3s"
              position="absolute"
              transform="translateX(-50%)"
              opacity={tooltip === "on" || isActive ? 1 : 0}
              top={
                tooltipPosition === "top"
                  ? `${-handleSize}px`
                  : `${handleSize / 2 + 4}px`
              }
              left={`${handlePosition[0]}%`}
            >
              {tooltipValue[0]}
            </Box>
          )}
          {isRS && tooltip !== "off" && (
            <Box
              p="0.1875rem 0.5rem"
              mt="-1rem"
              fontSize="0.875rem"
              bg={`${colorScheme}.500`}
              color={`${colorScheme}.50`}
              borderRadius="sm"
              transition="opacity 0.3s"
              position="absolute"
              transform="translateX(-50%)"
              opacity={tooltip === "on" || isActive ? 1 : 0}
              top={
                tooltipPosition === "top"
                  ? `${-handleSize}px`
                  : `${handleSize / 2 + 4}px`
              }
              left={`${handlePosition[1]}%`}
            >
              {tooltipValue[1]}
            </Box>
          )}

          {showSnapHints &&
            snapPoints.map(point => {
              const snapPos = getHandlePosition(point, this.props)
              let snapClassName = "sldr-snap-hint"
              if (handlePosition[0] <= snapPos && handlePosition[1] >= snapPos)
                snapClassName += " sldr-snap-hint-active"
              return (
                <Box
                  className={snapClassName}
                  key={`sldr-snap-hint-${point}`}
                  position="absolute"
                  top="-2px"
                  width="8px"
                  height="8px"
                  borderRadius="50%"
                  bg={`white`}
                  borderColor={`gray.200`}
                  borderWidth={1}
                  margin-left="-4px"
                  left={`${snapPos}%`}
                />
              )
            })}

          {ticks && (
            <Box
              className="sldr-axis"
              style={{
                position: "absolute",
                top: `${handleSize / 2}px`,
                left: 0,
                right: 0,
              }}
            >
              {ticks
                .filter(tick => !tickFormat || tickFormat(tick))
                .map(tick => (
                  <Box
                    key={`tick-${tick}`}
                    className="tick"
                    position="absolute"
                    left={`${getHandlePosition(tick, this.props)}%`}
                    transform="translateX(-50%)"
                    color={`gray.300`}
                    fontSize="0.875rem"
                    _first={{
                      transform: "translateX(-25%)",
                    }}
                  >
                    {tickFormat ? tickFormat(tick) : tick}
                  </Box>
                ))}
            </Box>
          )}
          <Box
            className="sldr-track"
            ref={this.getRef("track")}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${-handleSize / 2}px`,
              height: `${handleSize}px`,
              cursor: "pointer",
            }}
          />
          {isRS && (
            <Box
              className="sldr-range-track"
              ref={this.getRef("rangeTrack")}
              style={{
                position: "absolute",
                left: `${handlePosition[0]}%`,
                width: `${handlePosition[1] - handlePosition[0]}%`,
                top: `${-handleSize / 2}px`,
                height: `${handleSize}px`,
                cursor: "pointer",
              }}
            />
          )}
        </Box>
      </Box>
    )
  }
}

export default Slider
